import { Component, ViewChild, Input, HostBinding, forwardRef, AfterViewInit, output, DestroyRef, inject } from "@angular/core";
import { DropDownListComponent, DropDownSize } from "@progress/kendo-angular-dropdowns";
import { TooltipDirective } from "@progress/kendo-angular-tooltip";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { ITranslateObject } from "../../../views-interfaces";
import { WkTranslatePipe } from "@shared/index";

declare module "@progress/kendo-angular-dropdowns" {
    interface DropDownListComponent {
        name: string;
    }
}

@Component({
    selector: "kendo-dropdownlist-wrapper",
    templateUrl: "./kendo-dropdownlist-wrapper.component.html",
    standalone: false,
    host: {
        class: "wk-ddlw",
        "cg3-theme": "PDS-v3",
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => KendoDropDownListWrapper),
            multi: true,
        },
        WkTranslatePipe,
    ],
})
class KendoDropDownListWrapper implements ControlValueAccessor, AfterViewInit {
    @Input() name: string;
    @Input() data: any[] = [];
    @Input() size: DropDownSize = "medium";
    @Input() textField = "label";
    @Input() valueField = "id";
    @Input() activeField = "active";
    @Input() flowClass = "";
    @Input() virtual = false;
    readonly change = output<any>();
    @Input() label?: string | ITranslateObject = "";
    @Input() valuePrimitive = true;
    @Input() isGroupingEnabled = false;
    @Input() tooltipIfDisabled?: string;
    @Input() tooltipIfReadonly?: string;

    private static nextId = 0;
    readonly hostId = `wk-ddlw-${KendoDropDownListWrapper.nextId++}`;

    @HostBinding("attr.id")
    get hostIdAttr(): string {
        return this.hostId;
    }

    private _disabled = false;
    private _inputDisabled = false;
    @Input()
    set disabled(val: boolean) {
        this._inputDisabled = val;
        this.setDisabledState(val);
    }
    get disabled() {
        return this._disabled;
    }

    private _readonly = false;
    private _inputReadonly = false;
    @Input()
    set readonly(val: boolean) {
        this._inputReadonly = val;
        this.setReadonlyState(val);
    }
    get readonly() {
        return this._readonly;
    }

    private setReadonlyState(isReadonly: boolean): void {
        this._readonly = isReadonly || this._inputReadonly;
        this.applyReadonlyState();
    }

    private applyReadonlyState(): void {
        if (this.inputElement) {
            this.inputElement.setAttribute("readonly", String(this._readonly));
            this.inputElement.setAttribute("aria-readonly", String(this._readonly));
        }
    }

    @ViewChild("dropdownList") dropdownList!: DropDownListComponent;
    inputElement: HTMLElement | null = null;

    constructor() {
        const destroyRef = inject(DestroyRef);
        destroyRef.onDestroy(() => {
            this.inputElement = null;
        });
    }

    ngAfterViewInit(): void {
        this.updateInputElement();
        this.applyDisabledState();
        this.applyReadonlyState();
    }

    private updateInputElement(): void {
        if (this.dropdownList && this.dropdownList.wrapper) {
            this.inputElement = this.dropdownList.wrapper.nativeElement.querySelector(".k-input-inner");
            if (this.inputElement) {
                this.inputElement.setAttribute("data-input", "true");
                this.inputElement.setAttribute("disabled", String(this._disabled));
                this.inputElement.setAttribute("readonly", String(this._readonly));
                this.inputElement.setAttribute("aria-disabled", String(this._disabled));
                this.inputElement.setAttribute("aria-readonly", String(this._readonly));
            }
        }
    }

    private applyDisabledState(): void {
        if (this.inputElement) {
            this.inputElement.setAttribute("disabled", String(this._disabled));
            this.inputElement.setAttribute("aria-disabled", String(this._disabled));
        }
    }

    public onItemDisabled = (args: { dataItem: any; index: number }): boolean => {
        const item = args.dataItem;
        const keys = Object.keys(item);
        if (!this.activeField || !keys.includes(this.activeField)) return false;

        return item[this.valueField] !== this.value && !item[this.activeField];
    };

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._disabled = isDisabled || this._inputDisabled;
        this.applyDisabledState();
    }

    handleValueChange(newValue: any): void {
        const item = this.findByValueField(newValue);
        if (item && "disabled" in item && item.disabled) {
            newValue = this.value;
        }
        this.value = newValue;
        if (this._combobox) {
            this._combobox.value = newValue === "" ? null : newValue;
        }
        this.onChange(newValue);
        this.change.emit({ target: { value: newValue } });
    }

    findByValueField(value: any): any | undefined {
        const keys = this.valueField.split(".");

        return this.data.find((item) => {
            let current: any = item;
            for (const key of keys) {
                if (current && key in current) {
                    current = current[key];
                } else {
                    return false;
                }
            }
            return current === value;
        });
    }

    private onChange: (value: any) => void = () => {};
    public onTouched: () => void = () => {};
    private _value: any = null;
    @Input()
    set value(val: any) {
        this.writeValue(val);
    }
    get value() {
        return this._value;
    }

    writeValue(obj: any): void {
        this._value = obj;
        if (this._combobox) {
            this._combobox.value = obj === "" ? null : obj;
        }
    }

    private _combobox!: DropDownListComponent;
    @ViewChild(DropDownListComponent, { static: false })
    set combobox(component: DropDownListComponent) {
        if (component && this._combobox == null) {
            this._combobox = component;
            this.initializeComboBoxComponent(this._combobox);
        }
    }
    get combobox(): DropDownListComponent {
        return this._combobox;
    }

    initializeComboBoxComponent(component: DropDownListComponent): void {
        component.name = this.name;
    }

    @HostBinding("class")
    get hostClasses(): string {
        const classes = [];

        if (this.flowClass) {
            classes.push(this.flowClass);
        }

        return classes.join(" ");
    }

    @HostBinding("class.wk-ddlw--open")
    get hostOpenClass() {
        return this.isOpen;
    }

    isOpen = false;

    onOpen(): void {
        this.isOpen = true;

        if (this.isGroupingEnabled) {
            this.groupSectionVisualSeparation();
        }
    }

    onClose(): void {
        this.isOpen = false;
    }

    @HostBinding("attr.size")
    get hostSize() {
        return this.size;
    }

    @ViewChild(TooltipDirective) tooltipDir!: TooltipDirective;
    private showTimeout: ReturnType<typeof setTimeout> | undefined;
    private tooltipVisible = false;
    private lastHoveredEl: HTMLElement;

    onMouseOver(e: MouseEvent): void {
        const target = e.target as HTMLElement;
        const container =
            target?.classList?.contains("k-list-item") || target?.classList?.contains("k-input-inner")
                ? target
                : (target?.closest(".k-list-item, .k-input-inner") as HTMLElement);

        if (this.lastHoveredEl === container) return;
        this.lastHoveredEl = container;

        const isValidTarget = container && (container.classList.contains("k-list-item") || container.classList.contains("k-input-inner"));

        if (this.showTimeout) clearTimeout(this.showTimeout);

        if (this.tooltipVisible) this.onMouseLeave();

        if (!isValidTarget) return;

        const textEl = container.querySelector<HTMLElement>(".dropdownlist-wrapper-item, .k-input-value-text");
        if (!textEl) return;

        const isOverflowing = textEl.scrollWidth > textEl.clientWidth || textEl.scrollHeight > textEl.clientHeight;
        if (!isOverflowing) return;

        this.showTimeout = setTimeout(() => {
            this.tooltipDir.show(container);
            this.tooltipVisible = true;
        }, 300);
    }

    onMouseLeave(): void {
        if (this.showTimeout) clearTimeout(this.showTimeout);
        this.lastHoveredEl = null;
        if (!this.tooltipVisible) return;
        if (!this.tooltipDir.popupRef) return;

        this.tooltipVisible = false;
        this.tooltipDir.popupRef.popupElement.classList.add("cg3-tooltip-closing");
        setTimeout(() => {
            this.tooltipDir.hide();
        }, 300);
    }

    private groupSectionVisualSeparation(): void {
        const popupEl = this.dropdownList?.popupRef?.popupElement as HTMLElement | undefined;
        const list = popupEl.querySelectorAll(".k-list-ul .k-list-group-item");

        if (list && list.length > 0) {
            list.forEach((el) => {
                (el as HTMLElement).style.setProperty("height", "0px", "important");
                if (el === list[0]) {
                    (el as HTMLElement).style.setProperty("padding-top", "inherit");
                    (el as HTMLElement).style.setProperty("margin-top", "-5px");
                }
            });
        }
    }
}

export { KendoDropDownListWrapper };
